import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import scraperApi from "api";
import { useUser } from "routes/dataroutes/UserData";
import {
  DomainMultiplierResponse,
  LegacyRateOptions,
  extractQuoteDomains,
  highestLegacyRate,
  legacyRateFromQuote,
} from "utils/legacyRate";

async function fetchQuote(
  cache: Map<string, DomainMultiplierResponse>,
  domain: string,
  signal: AbortSignal
): Promise<DomainMultiplierResponse | null> {
  const cached = cache.get(domain);
  if (cached) return cached;
  try {
    const quote = (await scraperApi.multipliers.getDomainMultiplier(domain, { signal })) as DomainMultiplierResponse | null;
    if (!quote?.multipliers) return null;
    // An older API build (mid rolling deploy) omits standardMultipliers; caching that would hide the message until the page is left.
    if (quote.standardMultipliers) cache.set(domain, quote);
    return quote;
  } catch (err) {
    if (axios.isCancel(err)) throw err;
    // A failed quote is "no message", never an error shown to the user.
    return null;
  }
}

/**
 * Shared legacy-rate hook (ENG-1112). Takes the raw URL input (one URL per
 * line) and the request options, quotes each distinct domain once, and returns
 * the standard price to warn about when the account is on the old rate for any
 * of them, or undefined. Debounced and aborted on change like useProjectCostEstimate.
 *
 * Pass `undefined` for `input` when there is nothing to quote (SDE project
 * types, uploaded or webhook input) and no request fires.
 */
export function useLegacyRate(
  input: string | undefined,
  options: LegacyRateOptions,
  { debounceMs = 300 }: { debounceMs?: number } = {}
): number | undefined {
  const user = useUser();
  const [quotes, setQuotes] = useState<Array<DomainMultiplierResponse | null>>([]);
  // Quotes are what *this* account is billed, so a new account or entitlement starts an empty cache.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cache = useMemo(() => new Map<string, DomainMultiplierResponse>(), [user?.id, user?.newMultipliersEnabled]);
  const domainsKey = useMemo(() => extractQuoteDomains(input).join("\n"), [input]);

  useEffect(() => {
    // Drop the old quotes at once, so the old domain's price never sits next to the new cost.
    setQuotes([]);
    if (!domainsKey) return;

    const controller = new AbortController();
    const timer = setTimeout(() => {
      Promise.all(domainsKey.split("\n").map((domain) => fetchQuote(cache, domain, controller.signal)))
        .then((result) => {
          if (!controller.signal.aborted) setQuotes(result);
        })
        .catch(() => {}); // only an abort lands here
    }, debounceMs);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [domainsKey, cache, debounceMs]);

  // Every quote holds all six rows, so changing the options re-reads them without a new request.
  return highestLegacyRate(quotes.map((quote) => legacyRateFromQuote(quote, options)));
}
